/**
 * 1.5.16 — the agreements on a person's card: one pattern for all four (the housekeeping release, the Companion &
 * Wellness tenant waiver, the Tech Support agreement, the volunteer waiver). Each line says where it stands (not
 * sent · sent on <date> · completed <date> and how) and offers Send by email / Resend and Complete it for them;
 * the card's documents are View documents and Add documents.
 *
 * The texts are the ones the app already holds (shared/documents.ts, shared/agreements.ts): nothing is rewritten
 * here, and the hash recorded with a signature is the hash of that text.
 */
import { z } from 'zod';
import { SIGN_DOCS, VOLUNTEER_WAIVER } from './documents';
import { TECH_RELEASE_CURRENT, parseAgreement } from './agreements';
import type { Lang } from './email';

export const CARD_AGREEMENTS = ['housekeeping-release', 'companion-waiver', 'tech-agreement', 'volunteer-waiver'] as const;
export type CardAgreement = (typeof CARD_AGREEMENTS)[number];
export const TENANT_AGREEMENTS = ['housekeeping-release', 'companion-waiver', 'tech-agreement'] as const satisfies readonly CardAgreement[];
export type TenantAgreement = (typeof TENANT_AGREEMENTS)[number];
export const isCardAgreement = (s: unknown): s is CardAgreement => (CARD_AGREEMENTS as readonly unknown[]).includes(s);
export const isTenantAgreement = (s: unknown): s is TenantAgreement => (TENANT_AGREEMENTS as readonly unknown[]).includes(s);

/** The document's name in an email and on the card's audit line (the card itself uses the i18n keys). */
export const AGREEMENT_TITLE: Record<CardAgreement, Record<Lang, string>> = {
  'housekeeping-release': { en: 'Housekeeping Release', es: 'Liberación del programa de limpieza' },
  'companion-waiver': { en: 'Companion & Wellness Tenant Waiver', es: 'Renuncia del inquilino de Acompañamiento y bienestar' },
  'tech-agreement': { en: 'Tech Support Agreement', es: 'Acuerdo de Soporte técnico' },
  'volunteer-waiver': { en: 'Volunteer Waiver', es: 'Renuncia del voluntario' },
};

/**
 * The Tech Support agreement as the markdown the app's screens and PDFs lay out (`## ` headings, `- ` lists). Only
 * the marks change: every word is counsel's, in order. The hash recorded is the hash of counsel's own text.
 */
export function techAgreementMarkdown(text: string = TECH_RELEASE_CURRENT.text): string {
  const p = parseAgreement(text);
  const out: string[] = [`## ${p.title}`, `${p.subtitle} · ${p.place}`, `**In plain words:** ${p.plainWords}`, p.intro];
  for (const s of p.sections) {
    out.push(`## ${s.n}. ${s.title}`);
    for (const b of s.blocks) out.push(b.kind === 'p' ? b.text : b.items.map((i) => `- ${i}`).join('\n'));
  }
  return out.filter(Boolean).join('\n\n');
}

export interface AgreementText {
  version: string;
  /** The languages the text exists in (the Tech Support agreement is counsel's English only). */
  langs: readonly Lang[];
  /** Markdown, as shown and as laid out in the PDF. */
  text: (lang: Lang) => string;
  /** What is hashed: the stored text itself (for the Tech Support agreement, counsel's own file — its fixed SHA-256). */
  fixedHash: string | null;
  /** A draft translation awaiting the Director's review (shown as a note). */
  draft: (lang: Lang) => boolean;
}

export const AGREEMENT_TEXT: Record<CardAgreement, AgreementText> = {
  'housekeeping-release': { version: SIGN_DOCS['housekeeping-release'].version, langs: ['en', 'es'], text: (l) => SIGN_DOCS['housekeeping-release'].text[l], fixedHash: null, draft: (l) => !!SIGN_DOCS['housekeeping-release'].reviewNote[l] },
  'companion-waiver': { version: SIGN_DOCS['companion-waiver'].version, langs: ['en', 'es'], text: (l) => SIGN_DOCS['companion-waiver'].text[l], fixedHash: null, draft: (l) => !!SIGN_DOCS['companion-waiver'].reviewNote[l] },
  'tech-agreement': { version: TECH_RELEASE_CURRENT.version, langs: ['en'], text: () => techAgreementMarkdown(), fixedHash: TECH_RELEASE_CURRENT.sha256, draft: () => false },
  'volunteer-waiver': { version: VOLUNTEER_WAIVER.version, langs: ['en', 'es'], text: (l) => VOLUNTEER_WAIVER.text[l], fixedHash: null, draft: (l) => !!VOLUNTEER_WAIVER.reviewNote[l] },
};

/** The language an agreement is shown in for this person (English when it has no text in theirs). */
export const agreementLang = (kind: CardAgreement, lang: Lang): Lang => (AGREEMENT_TEXT[kind].langs.includes(lang) ? lang : 'en');

/**
 * The email that asks for a signature. PLACEHOLDER WORDING (1.5.16): the Director's approved wording
 * (docs/EMAIL-WORDING-2026-10.md) has no "please sign" email, so these plain words are used until he writes one
 * (NEEDS-ELI). A site template row with the code AGREEMENT_SIGN (and AGREEMENT_SIGN_ES) replaces them when it exists.
 */
export const AGREEMENT_SIGN_CODE = 'AGREEMENT_SIGN';
export const AGREEMENT_SIGN_WORDING: Record<Lang, { subject: string; body: string; button: string }> = {
  en: {
    subject: 'Please read and sign: [DocumentTitle]',
    body: '# Please read and sign\n\nGreetings, [TenantPreferredName].\n\nThe Associated Blind Foundation needs your signature on the [DocumentTitle]. Press the button below to open it. It can be read aloud to you. When you are ready, type your name and agree.\n\nIf you would rather go through it with someone from the office, call us at [OfficePhone] and we will read it with you.\n\nWarm regards,\nAssociated Blind Foundation',
    button: 'Read and sign',
  },
  es: {
    subject: 'Por favor lea y firme: [DocumentTitle]',
    body: '# Por favor lea y firme\n\nSaludos, [TenantPreferredName].\n\nAssociated Blind Foundation necesita su firma en: [DocumentTitle]. Pulse el botón de abajo para abrirlo. Se le puede leer en voz alta. Cuando esté listo, escriba su nombre y acepte.\n\nSi prefiere revisarlo con alguien de la oficina, llámenos al [OfficePhone] y lo leeremos con usted.\n\nUn cordial saludo,\nAssociated Blind Foundation',
    button: 'Leer y firmar',
  },
};

/** How long a tenant's own signing link lives. */
export const AGREEMENT_LINK_DAYS = 30;

export type AgreementHow = 'office' | 'link' | 'paper' | 'onFile';

/** One agreement on the card. */
export interface AgreementLine {
  kind: CardAgreement;
  version: string;
  /** The last email asking for it (null: never sent, or the last one was taken back in the Outbox). */
  sent: { at: string; to: string; by: string; left: boolean } | null;
  /** 1.8.12: the tenant's own link was opened (first time, how many times) and the agreement is not on file. Absent when it was never opened. */
  opened?: { firstAt: string; opens: number } | null;
  /** Null while it is not on file. */
  completed: { at: string; how: AgreementHow; by: string | null } | null;
  /** Why nothing can be done with it yet (not in the program, the list is not on the site…) — the app's words. */
  blocked: string | null;
  /** Why only Complete it for them is off (a volunteer whose "18 or older" is not answered) — the app's words. */
  noComplete: string | null;
  /** Why Send by email is off, when it is: the button stays, disabled, with these words. */
  noSend: 'noEmail' | 'emailNotAllowed' | 'optedOut' | 'linksOff' | null;
}

export interface AgreementLinesResponse {
  personId: string;
  who: 'tenant' | 'volunteer';
  name: string;
  email: string | null;
  lang: Lang;
  lines: AgreementLine[];
}

/** The email exactly as it will go, for the review before the press. */
export interface AgreementEmailPreview {
  kind: CardAgreement;
  code: string;
  to: string;
  lang: Lang;
  subject: string;
  body: string;
  button: string;
  office: { phone: string | null; email: string | null };
  sendAt: string;
  /** Minutes of the cancel window (null: it leaves the next morning). */
  windowMinutes: number | null;
  /** True while the placeholder wording is in use. */
  placeholder: boolean;
}

export interface AgreementSendResponse extends AgreementLinesResponse {
  communicationId: string | null;
  sendAt: string;
  message: string;
}

export const OnBehalfSchema = z.object({
  typedName: z.string().trim().min(2, 'Type the person’s full name.').max(120),
  agree: z.literal(true, { errorMap: () => ({ message: 'Tick “I agree” first.' }) }),
  hash: z.string().regex(/^[0-9a-fA-F]{64}$/),
  lang: z.enum(['en', 'es']),
});
export type OnBehalfInput = z.infer<typeof OnBehalfSchema>;

export interface OnBehalfResponse extends AgreementLinesResponse {
  kind: CardAgreement;
  /** The filed PDF's name in the person's folder. */
  file: string;
  message: string;
}

/** A file in the person's folder (View documents): everything there, on the record or not. */
export interface FolderFile {
  /** The path under the person's folder ("2026-10-06 Waiver.pdf", "Tech Support/Agreements/…pdf"). */
  path: string;
  name: string;
  /** When it was last changed (ISO), newest first. */
  at: string;
  size: number;
  /** False for a file someone put in the folder by hand (the drop-in check lists it too). */
  onRecord: boolean;
}

export interface FolderFilesResponse {
  folder: string;
  files: FolderFile[];
}

/** The tenant's own page for an agreement sent by email (/link/<token>, kind tenant-agreement). */
export interface AgreementLinkPage {
  state: 'ok' | 'answered' | 'closed';
  kind: TenantAgreement;
  lang: Lang;
  firstName: string;
  version: string;
  completedAt: string | null;
  officePhone: string | null;
}
